import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface Vehicle360ViewerProps {
  imagePath: string;
  vehicleName: string;
}

const Vehicle360Viewer: React.FC<Vehicle360ViewerProps> = ({ imagePath, vehicleName }) => {
  const { t } = useTranslation();
  const viewerRef = useRef<HTMLDivElement>(null);
  const pannellumInstanceRef = useRef<any>(null);

  useEffect(() => {
    let isMounted = true; // Prevent duplicate initialization in StrictMode

    const initPannellum = () => {
      if (!isMounted) return;

      if (viewerRef.current && (window as any).pannellum) {
        // Double check height before initializing
        if (viewerRef.current.offsetHeight === 0) {
          console.error('Container height is 0, cannot initialize Pannellum');
          return;
        }

        // Destroy existing instance if any
        if (pannellumInstanceRef.current) {
          try {
            pannellumInstanceRef.current.destroy();
          } catch (e) {
            console.error('Error destroying previous Pannellum viewer:', e);
          }
          pannellumInstanceRef.current = null;
        }

        try {
          // Use absolute URL for the image
          const absoluteImagePath = window.location.origin + imagePath;

          if (!isMounted) return;

          pannellumInstanceRef.current = (window as any).pannellum.viewer(viewerRef.current, {
            type: 'equirectangular',
            panorama: absoluteImagePath,
            autoLoad: true,
            showControls: true,
            showFullscreenCtrl: true,
            showZoomCtrl: true,
            mouseZoom: true,
            doubleClickZoom: true,
            draggable: true,
            keyboardZoom: true,
            compass: false,
            // Initial view settings
            pitch: 0,
            yaw: 0,
            hfov: 100,
            minHfov: 50,
            maxHfov: 120,
            // Auto rotate (optional)
            autoRotate: 0,
          });
        } catch (error) {
          console.error('Error initializing Pannellum:', error);
        }
      }
    };

    // Check if Pannellum is already loaded
    if ((window as any).pannellum) {
      // Add small delay to ensure container is fully rendered
      setTimeout(initPannellum, 100);
    } else {
      // Check if script is already loading/loaded
      const existingScript = document.querySelector('script[src*="pannellum"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          setTimeout(initPannellum, 100);
        });
      } else {
        // Load Pannellum CSS
        const existingLink = document.querySelector('link[href*="pannellum"]');
        if (!existingLink) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css';
          document.head.appendChild(link);
        }

        // Load Pannellum Script
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js';
        script.async = false;
        script.onload = () => {
          setTimeout(initPannellum, 100);
        };
        document.body.appendChild(script);
      }
    }

    // Cleanup
    return () => {
      isMounted = false;

      if (pannellumInstanceRef.current) {
        try {
          pannellumInstanceRef.current.destroy();
        } catch (e) {
          console.error('Error destroying Pannellum viewer:', e);
        }
        pannellumInstanceRef.current = null;
      }
    };
  }, [imagePath]);

  return (
    <div className="relative">
      {/* Pannellum Container */}
      <div
        ref={viewerRef}
        className="w-full bg-gray-900"
        style={{ position: 'relative', width: '100%', height: window.innerWidth < 768 ? '250px' : '500px' }}
      />

      {/* Instructions */}
      <div className="px-2 py-2 md:px-3 md:py-3">
        <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
          <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <span className="hidden md:inline">{t('vehicle360.desktopInstructions')}</span>
          <span className="md:hidden">{t('vehicle360.mobileInstructions')}</span>
        </div>
      </div>
    </div>
  );
};

export default Vehicle360Viewer;
